/**
 * UI. Deliberately spare: one gate button, now-playing, volumes, status.
 *
 * There are no transport controls of any kind, and `setSpaceInert` is called
 * after the gate so Space cannot act as a hidden pause.
 *
 * `#background` is rendered now and left empty so background imagery can be
 * added later without restructuring.
 */

export interface DisclosureView {
  id: string;
  description: string;
  severity?: string;
  baseScore?: number;
  published?: string;
}

export interface UiState {
  state:
    | "idle"
    | "loading"
    | "fetching"
    | "playing"
    | "preparing"
    | "no-voice"
    | "error";
  nowPlaying?: string | null;
  meta?: string | null;
  status?: string | null;
  slot?: number | null;
  progress?: { percent: number; label?: string } | null;
  disclosure?: DisclosureView | null;
}

export interface UiHandlers {
  onStart: () => void;
  onVoiceVolume: (value: number) => void;
  onMusicVolume: (value: number) => void;
}

export interface Ui {
  setState(state: UiState): void;
  setSpaceInert(inert: boolean): void;
  setProgress(percent: number, label?: string): void;
  /** Replace only the status line, leaving the now-playing area untouched. */
  setStatus(message: string): void;
  setDisclosure(disclosure: DisclosureView | null): void;
  dispose(): void;
}

function required<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`missing UI element: ${selector}`);
  return element;
}

export function createUi(root: Document, handlers: UiHandlers): Ui {
  const start = required<HTMLButtonElement>(root, "#start");
  const gate = required<HTMLElement>(root, ".gate");
  const nowPlaying = required<HTMLElement>(root, "#now-playing");
  const nowPlayingMeta = required<HTMLElement>(root, "#now-playing-meta");
  const slotLabel = required<HTMLElement>(root, "#slot");
  const status = required<HTMLElement>(root, "#status");
  const statusText = required<HTMLElement>(root, "#status-text");
  const loading = required<HTMLElement>(root, "#loading");
  const loadingLabel = required<HTMLElement>(root, "#loading-label");
  const progress = required<HTMLElement>(root, "#progress");
  const progressFill = required<HTMLElement>(root, "#progress-fill");
  const voiceVolume = required<HTMLInputElement>(root, "#voice-volume");
  const musicVolume = required<HTMLInputElement>(root, "#music-volume");
  const disclosureText = required<HTMLElement>(root, "#disclosure-text");
  const disclosureTags = required<HTMLElement>(root, "#disclosure-tags");
  const disclosureBody = required<HTMLElement>(root, "#disclosure-body");
  const disclosurePanel = required<HTMLElement>(root, ".disclosure");

  /** The single status surface: one dot, one short label, five possible states. */
  const setSignal = (state: string, text: string) => {
    status.dataset.state = state;
    statusText.textContent = text;
  };

  let spaceInert = false;
  let shownId: string | null = null;
  let driftFrame: number | null = null;
  let driftPaused = false;

  const onStartClick = () => handlers.onStart();
  const onVoiceInput = () => handlers.onVoiceVolume(Number(voiceVolume.value) / 100);
  const onMusicInput = () => handlers.onMusicVolume(Number(musicVolume.value) / 100);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code !== "Space" && event.key !== " ") return;
    const target = event.target as HTMLElement | null;
    if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
    // Space is the gate's activation key once. Afterwards it is inert.
    if (spaceInert) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    handlers.onStart();
  };

  start.addEventListener("click", onStartClick);
  voiceVolume.addEventListener("input", onVoiceInput);
  musicVolume.addEventListener("input", onMusicInput);
  root.addEventListener("keydown", onKeyDown);

  const setProgressState = (percent: number, label?: string) => {
    const clamped = Math.min(100, Math.max(0, Math.round(percent)));
    progress.removeAttribute("aria-hidden");
    progress.setAttribute("role", "progressbar");
    progress.setAttribute("aria-valuenow", String(clamped));
    progressFill.style.width = `${clamped}%`;
    if (label) loadingLabel.textContent = label;
  };

  /**
   * Take the bar out of the accessibility tree and out of the layout when
   * loading is over. Leaving a completed progressbar behind is misleading:
   * assistive tech would keep announcing a download that has finished.
   */
  const clearProgress = (): void => {
    progress.setAttribute("aria-hidden", "true");
    progress.removeAttribute("role");
    progress.removeAttribute("aria-valuenow");
    progressFill.style.width = "0%";
  };

  const stopDrift = () => {
    if (driftFrame !== null) cancelAnimationFrame(driftFrame);
    driftFrame = null;
  };

  /**
   * Let a long disclosure read itself, slowly enough to stay background. Any
   * manual scroll wins: the listener asked for a position, and overriding it
   * would be a control they did not ask for.
   */
  const startDrift = () => {
    stopDrift();
    const reduced = root.defaultView?.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    // Measured after a frame: setting the text and reading scrollHeight in the
    // same task can see a panel that has not been laid out yet, which looks like
    // "nothing to scroll" and silences the drift for good.
    driftFrame = requestAnimationFrame(() => {
      driftFrame = null;
      if (driftPaused || shownId === null) return;
      if (disclosureBody.scrollHeight - disclosureBody.clientHeight < 8) return;
      let previous = performance.now();
      // Tracked here rather than read back: a fractional scroll offset is not
      // retained across frames, so incrementing the element by 0.1px a frame
      // would never move at all.
      let offset = disclosureBody.scrollTop;
      const step = (now: number) => {
        const elapsed = Math.min(120, now - previous);
        previous = now;
        const remaining = disclosureBody.scrollHeight - disclosureBody.clientHeight;
        if (offset >= remaining - 1) {
          driftFrame = null;
          return;
        }
        offset = Math.min(remaining, offset + (elapsed / 1000) * 6.5);
        disclosureBody.scrollTop = Math.round(offset);
        driftFrame = requestAnimationFrame(step);
      };
      driftFrame = requestAnimationFrame(step);
    });
  };

  /**
   * Reveal without reflowing. On wide screens the panel's column is laid out from
   * the start, so narration arriving fades a surface in rather than pushing the
   * broadcast sideways; on narrow screens it is below everything, so it only
   * extends the page.
   */
  const setDisclosureVisible = (visible: boolean) => {
    if (disclosurePanel.dataset.visible === String(visible)) return;
    disclosurePanel.dataset.visible = String(visible);
    disclosurePanel.setAttribute("aria-hidden", String(!visible));
    disclosurePanel.inert = !visible;
  };

  const showDisclosure = (disclosure: DisclosureView | null) => {
    if (!disclosure) {
      setDisclosureVisible(false);
      if (shownId !== null) {
        shownId = null;
        disclosureText.textContent = "";
        disclosureTags.textContent = "";
      }
      return;
    }
    if (shownId === disclosure.id) return;
    shownId = disclosure.id;
    setDisclosureVisible(true);
    stopDrift();
    disclosureBody.scrollTop = 0;
    driftPaused = false;
    disclosureText.textContent = disclosure.description;
    const bits: string[] = [];
    if (disclosure.severity) bits.push(disclosure.severity.toLowerCase());
    if (typeof disclosure.baseScore === "number") bits.push(disclosure.baseScore.toFixed(1));
    if (disclosure.published) bits.push(disclosure.published.slice(0, 10));
    disclosureTags.textContent = bits.join(" · ");
    disclosureTags.dataset.severity = disclosure.severity?.toLowerCase() ?? "";
    const reduced = root.defaultView?.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduced && typeof disclosureText.animate === "function") {
      disclosureText.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 1_600,
        easing: "ease-out",
      });
    }
    startDrift();
  };

  const onDisclosureScroll = () => {
    driftPaused = true;
    stopDrift();
  };
  disclosureBody.addEventListener("wheel", onDisclosureScroll, { passive: true });
  disclosureBody.addEventListener("touchstart", onDisclosureScroll, { passive: true });

  return {
    setState(next) {
      switch (next.state) {
        case "idle":
          setSignal("idle", next.status ?? "Standing by");
          showDisclosure(null);
          break;
        case "loading":
          loading.hidden = false;
          setSignal("tuning", "Tuning in");
          setProgressState(next.progress?.percent ?? 0, next.progress?.label);
          break;
        case "fetching":
          loading.hidden = true;
          setSignal("tuning", "Tuning in");
          break;
        case "preparing":
          loading.hidden = true;
          setSignal("tuning", "Tuning in");
          break;
        case "playing":
          loading.hidden = true;
          if (next.nowPlaying) {
            nowPlaying.innerHTML = "";
            const link = root.createElement("a");
            link.href = `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(next.nowPlaying)}`;
            link.textContent = next.nowPlaying;
            link.rel = "noreferrer noopener";
            link.target = "_blank";
            nowPlaying.append(link);
          } else {
            nowPlaying.textContent = "—";
          }
          nowPlayingMeta.textContent = next.meta ?? "";
          // "Broadcasting" means a voice is speaking, not that the player has
          // started. Until the first narration begins the station is still
          // tuning in, with only the bed on air.
          if (next.nowPlaying) setSignal("live", "Broadcasting");
          else setSignal("tuning", "Tuning in");
          showDisclosure(next.disclosure ?? null);
          if (next.slot !== undefined && next.slot !== null) {
            slotLabel.hidden = false;
            slotLabel.textContent = `slot ${next.slot}`;
          }
          break;
        case "no-voice":
          loading.hidden = true;
          status.textContent =
            "No local speech voice is available on this device. Music continues; narration does not.";
          nowPlaying.textContent = "—";
          nowPlayingMeta.textContent = "";
          break;
        case "error":
          loading.hidden = true;
          setSignal("error", next.status ?? "Something went wrong");
          break;
      }
      if (next.state !== "loading") {
        clearProgress();
      }
    },
    setSpaceInert(inert) {
      spaceInert = inert;
      if (inert) {
        // No explanatory copy: once audio is running there is nothing left to
        // explain, and listing the controls we do *not* have tells the listener
        // about a wall clock they can already hear.
        start.disabled = true;
        gate.classList.add("gate--done");
      }
    },
    setProgress(percent, label) {
      // Once every file is loaded there is no download left to report. Keep the
      // block up and it sits at 100% for however long the wall clock still owes
      // before the next disclosure can start — which reads as a stalled bar, and
      // is what a listener sees as "stuck at 73%" if they catch it mid-load.
      // The status line already explains the remaining wait.
      if (percent >= 100) {
        loading.hidden = true;
        clearProgress();
        return;
      }
      loading.hidden = false;
      setProgressState(percent, label);
    },
    setStatus(message) {
      // Only the words: the indicator keeps its dot and state, and the
      // now-playing area keeps showing the current CVE.
      statusText.textContent = message;
    },
    setDisclosure: showDisclosure,
    dispose() {
      start.removeEventListener("click", onStartClick);
      voiceVolume.removeEventListener("input", onVoiceInput);
      musicVolume.removeEventListener("input", onMusicInput);
      root.removeEventListener("keydown", onKeyDown);
      disclosureBody.removeEventListener("wheel", onDisclosureScroll);
      disclosureBody.removeEventListener("touchstart", onDisclosureScroll);
      stopDrift();
    },
  };
}
